<script setup lang="ts">
/**
 * SiteHeader — 顶部导航 + 移动端左侧抽屉（Nexus Cyberpunk 玻璃态）
 * - 桌面端：横向 nav + LanguageSwitch + hamburger 隐藏
 * - 移动端：横向 nav 隐藏 + hamburger 显示 + 左侧 off-canvas drawer
 * - 深色唯一（<html class="dark"> 恒在），无浅/深切换
 * - 玻璃态 sticky 顶栏：半透明深底 + backdrop blur + 霓虹描边
 *
 * ⚠️ 未来入口结构预留（本次不渲染，避免死链）：
 *   - 登录入口：计划放桌面 nav 右侧（tools 区）渐变 CTA 按钮，路由 /login
 *   - 价格 / 订阅页：计划放 nav 主区，路由 /pricing 或 /subscribe
 *   后续新增页面时，在 desktopNavItems / mainNavItems 追加条目并加 i18n key，
 *   在 tools 区追加登录 CTA 即可，无需改动布局结构。
 *   详见 docs/maindesign.md §Header 规划。
 *
 * 锚点跳转逻辑迁到 composables/useAnchorNav.ts，本组件只保留 drawer 关闭副作用。
 * 跨页 hash 跳转靠 useAnchorNav，同步把自身高度暴露到 --yfb-header-h 供 Home 各 section
 * scroll-margin-top 引用（避开 sticky header 遮挡）。见 docs/mainadr/0008-anchor-navigation.md
 */
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import BrandMark from '@/components/BrandMark.vue'
import LanguageSwitch from '@/components/LanguageSwitch.vue'
import YfbIcon from '@/components/YfbIcon.vue'
import type { IconName } from '@/types/icons'
import { useAnchorNav } from '@/composables/useAnchorNav'
import { useHeaderMenu } from '@/composables/useHeaderMenu'
import { useAuthStore } from '@/stores/auth'
import { apiLogout } from '@/api/auth'

const { t } = useI18n()
const { go } = useAnchorNav()
const route = useRoute()
const auth = useAuthStore()

/** 导航当前页高亮（token：--yfb-nav-active-bg/fg，ADR-0012 预留） */
const isActive = (href: string) => route.path === href

/**
 * CTA 三态（ADR：未订阅登录用户常驻开通入口）：
 *  - 未登录 → 「登录」
 *  - 已登录未订阅 → 渐变主 CTA「开通 Pro 会员」→ /subscribe
 *  - 已订阅（entitlement.type==='active'）→ 账号入口
 *  entitlement 取自 auth store 已有快照（不另发请求）；数据未到位时按已订阅兜底，避免首屏闪烁
 */
const showUpgradeCta = computed(() => {
  if (!auth.isLoggedIn) return false
  return auth.userInfo?.entitlement?.type !== undefined && auth.userInfo.entitlement.type !== 'active'
})

/** 会员徽章（用户名旁）：真实订阅/兑换/邀请=PRO；试用=试用；过期=置灰PRO；免费/未登录=不显示 */
const memberBadge = computed<{ label: string; tone: 'active' | 'trial' | 'expired' } | null>(() => {
  const e = auth.userInfo?.entitlement
  if (!e || e.type === 'none') return null
  if (e.type === 'active') {
    return e.is_trial
      ? { label: t('account.memberBadge.trial'), tone: 'trial' }
      : { label: t('account.memberBadge.pro'), tone: 'active' }
  }
  return { label: t('account.memberBadge.pro'), tone: 'expired' }
})

const drawerOpen = ref(false)
const headerEl = ref<HTMLElement | null>(null)
let resizeObserver: ResizeObserver | null = null

// 账号下拉（2026-08-31）：点击昵称展开「前往个人中心 / 退出登录」，点击外部关闭；
// 与语言下拉共享 useHeaderMenu 互斥状态（打开其一自动收起另一个）
const headerAccountMenu = useHeaderMenu('account')
const accountMenuOpen = headerAccountMenu.open
const accountMenuRef = ref<HTMLElement | null>(null)
function toggleAccountMenu() {
  headerAccountMenu.toggle()
}
function closeAccountMenu() {
  headerAccountMenu.close()
}
function handleGlobalClick(e: MouseEvent) {
  if (accountMenuRef.value && !accountMenuRef.value.contains(e.target as Node)) {
    closeAccountMenu()
  }
}
// 前往个人中心：收起下拉 + 跳转 + 释放焦点（避免切页后按钮仍保持 focus）
function goAccount() {
  closeAccountMenu()
  void go('/account')
  if (typeof document !== 'undefined' && document.activeElement instanceof HTMLElement) {
    document.activeElement.blur()
  }
}
async function handleLogout() {
  closeAccountMenu()
  if (typeof window === 'undefined') return
  if (!window.confirm(t('account.logoutConfirm'))) return
  try {
    await apiLogout()
  } catch {
    // 网络异常也继续本地登出
  }
  auth.clear()
  void go('/')
}

// 主导航（精简）：订阅计划 / 安装指南 / 使用说明（Q3：去掉功能特性/终端支持/常见问题入口）
const mainNavItems: { key: string; href: string; icon: IconName }[] = [
  { key: 'subscribe', href: '/subscribe', icon: 'lucide:crown' },
  { key: 'install', href: '/install', icon: 'lucide:download' },
  { key: 'guide', href: '/guide', icon: 'lucide:book-open' }
]

// 工具页 4 项（桌面 nav 不可见，抽屉「关于」分组）
const utilityNavItems = [
  { key: 'home', href: '/' },
  { key: 'about', href: '/about' },
  { key: 'privacy', href: '/privacy' },
  { key: 'terms', href: '/terms' }
]

const desktopNavItems = mainNavItems

function openDrawer() {
  drawerOpen.value = true
}

function closeDrawer() {
  drawerOpen.value = false
}

function toggleDrawer() {
  drawerOpen.value = !drawerOpen.value
}

function handleNavItem(href: string, close = true) {
  // useAnchorNav 内部已处理同/跨页 hash 滚动与 replaceState 注 hash
  void go(href, close ? closeDrawer : undefined)
}

function handleKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape') closeDrawer()
}

watch(drawerOpen, (open) => {
  if (open) {
    document.body.style.overflow = 'hidden'
    document.addEventListener('keydown', handleKeydown)
  } else {
    document.body.style.overflow = ''
    document.removeEventListener('keydown', handleKeydown)
  }
})

onMounted(() => {
  // 客户端恢复登录态（SSR 期间跳过）
  auth.hydrate()
  // 账号下拉：点击页面其他区域关闭
  document.addEventListener('click', handleGlobalClick)
  // 把 header 真实高度同步到 --yfb-header-h，Home 各 section scroll-margin-top 引用
  if (headerEl.value && typeof ResizeObserver !== 'undefined') {
    const update = () => {
      if (headerEl.value) {
        document.documentElement.style.setProperty(
          '--yfb-header-h',
          `${headerEl.value.offsetHeight}px`
        )
      }
    }
    update()
    resizeObserver = new ResizeObserver(update)
    resizeObserver.observe(headerEl.value)
  }
})

onUnmounted(() => {
  document.removeEventListener('click', handleGlobalClick)
  document.body.style.overflow = ''
  document.removeEventListener('keydown', handleKeydown)
  resizeObserver?.disconnect()
})
</script>

<template>
  <header ref="headerEl" class="site-header" :class="{ 'site-header--drawer-open': drawerOpen }">
    <div class="site-header__inner">
      <button
        type="button"
        class="site-header__hamburger"
        :aria-expanded="drawerOpen"
        aria-label="Toggle menu"
        @click="toggleDrawer"
      >
        <span class="site-header__hamburger-line" :class="{ 'site-header__hamburger-line--open': drawerOpen }"></span>
        <span class="site-header__hamburger-line" :class="{ 'site-header__hamburger-line--open': drawerOpen }"></span>
        <span class="site-header__hamburger-line" :class="{ 'site-header__hamburger-line--open': drawerOpen }"></span>
      </button>

      <a href="/" class="site-header__brand-link" @click.prevent="handleNavItem('/', false)">
        <BrandMark size="md" variant="full" mono />
      </a>

      <nav class="site-header__nav">
        <a
          v-for="item in desktopNavItems"
          :key="item.key"
          :href="item.href"
          class="site-header__link"
          :class="{ 'site-header__link--active': isActive(item.href) }"
          :aria-current="isActive(item.href) ? 'page' : undefined"
          @click.prevent="handleNavItem(item.href)"
        >
          <YfbIcon :name="item.icon" :size="15" aria-hidden="true" />
          {{ t(`nav.${item.key}`) }}
        </a>
      </nav>

      <div class="site-header__tools">
        <!-- CTA 三态：未登录 → 登录；已登录未订阅 → 开通 Pro 会员；已订阅 → 账号入口 -->
        <a
          v-if="showUpgradeCta"
          :href="'/subscribe'"
          class="site-header__tool site-header__login site-header__login--upgrade"
          @click.prevent="handleNavItem('/subscribe')"
        >
          {{ t('nav.upgradeCta') }}
        </a>
        <!-- 已登录（订阅中）：昵称 → 下拉（前往个人中心 / 退出登录），不再直接跳页 -->
        <div v-else-if="auth.isLoggedIn" ref="accountMenuRef" class="site-header__acct">
          <button
            type="button"
            class="site-header__tool site-header__login site-header__login--account"
            :aria-expanded="accountMenuOpen"
            :aria-haspopup="'menu'"
            @click="toggleAccountMenu"
          >
            <span class="site-header__acct-avatar" aria-hidden="true">
              {{ (auth.userInfo?.name || auth.userInfo?.email || '?').slice(0, 1).toUpperCase() }}
            </span>
            <span class="site-header__acct-id">
              <span class="site-header__acct-name">{{ auth.userInfo?.name || t('nav.account') }}</span>
              <span
                v-if="memberBadge"
                class="site-header__member-badge"
                :class="`site-header__member-badge--${memberBadge.tone}`"
              >
                {{ memberBadge.label }}
              </span>
            </span>
            <YfbIcon
              name="lucide:chevron-down"
              :size="14"
              class="shrink-0 transition-transform duration-200"
              :class="{ 'rotate-180': accountMenuOpen }"
              aria-hidden="true"
            />
          </button>
          <div v-if="accountMenuOpen" class="site-header__acct-menu" role="menu">
            <a
              href="/account"
              class="site-header__acct-item"
              role="menuitem"
              @click.prevent="goAccount"
            >
              <YfbIcon name="lucide:settings" :size="15" aria-hidden="true" />
              {{ t('nav.accountCenter') }}
            </a>
            <button type="button" class="site-header__acct-item" role="menuitem" @click="handleLogout">
              <YfbIcon name="ph:power-bold" :size="15" aria-hidden="true" />
              {{ t('account.logout') }}
            </button>
          </div>
        </div>
        <a
          v-else
          :href="'/login'"
          class="site-header__tool site-header__login"
          @click.prevent="handleNavItem('/login')"
        >
          {{ t('nav.login') }}
        </a>
        <LanguageSwitch menu-id="header-lang" />
      </div>
    </div>

    <!-- 背板 -->
    <div
      class="site-header__backdrop"
      :class="{ 'site-header__backdrop--visible': drawerOpen }"
      @click="closeDrawer"
    ></div>

    <!-- 左侧抽屉 -->
    <aside
      class="site-header__drawer"
      :class="{ 'site-header__drawer--open': drawerOpen }"
      role="dialog"
      aria-label="Menu"
    >
      <div class="site-header__drawer-top">
        <a href="/" class="site-header__drawer-brand" @click.prevent="handleNavItem('/')">
          <BrandMark size="md" variant="full" mono />
        </a>
        <button
          type="button"
          class="site-header__drawer-close"
          aria-label="Close menu"
          @click="closeDrawer"
        >
          <span class="site-header__drawer-close-line" aria-hidden="true"></span>
          <span class="site-header__drawer-close-line" aria-hidden="true"></span>
        </button>
      </div>

      <nav class="site-header__drawer-nav">
        <div class="site-header__drawer-group">
          <span class="site-header__drawer-label">NAV</span>
          <a
            v-for="item in mainNavItems"
            :key="item.key"
            :href="item.href"
            class="site-header__drawer-link"
            :class="{ 'site-header__drawer-link--active': isActive(item.href) }"
            :aria-current="isActive(item.href) ? 'page' : undefined"
            @click.prevent="handleNavItem(item.href)"
          >
            {{ t(`nav.${item.key}`) }}
            <span class="site-header__drawer-arrow" aria-hidden="true">→</span>
          </a>
        </div>

        <div class="site-header__drawer-divider" aria-hidden="true"></div>

        <div class="site-header__drawer-group">
          <span class="site-header__drawer-label">ABOUT</span>
          <a
            v-for="item in mainNavItems"
            :key="item.key"
            :href="item.href"
            class="site-header__drawer-link"
            @click.prevent="handleNavItem(item.href)"
          >
            <span class="site-header__drawer-link-main">
              <YfbIcon :name="item.icon" :size="16" aria-hidden="true" />
              {{ t(`nav.${item.key}`) }}
            </span>
            <span class="site-header__drawer-arrow" aria-hidden="true">→</span>
          </a>
        </div>

        <!-- 抽屉底部：登录状态入口（移动端） -->
        <div class="site-header__drawer-divider" aria-hidden="true"></div>
        <div class="site-header__drawer-group">
          <span class="site-header__drawer-label">ACCOUNT</span>
          <a
            v-if="showUpgradeCta"
            :href="'/subscribe'"
            class="site-header__drawer-link"
            @click.prevent="handleNavItem('/subscribe')"
          >
            {{ t('nav.upgradeCta') }}
            <span class="site-header__drawer-arrow" aria-hidden="true">→</span>
          </a>
          <a
            v-if="auth.isLoggedIn"
            :href="'/account'"
            class="site-header__drawer-link site-header__drawer-link--utility"
            @click.prevent="handleNavItem('/account')"
          >
            {{ auth.userInfo?.name || t('nav.account') }}
            <span class="site-header__drawer-arrow" aria-hidden="true">→</span>
          </a>
          <button
            v-if="auth.isLoggedIn"
            type="button"
            class="site-header__drawer-link site-header__drawer-link--utility site-header__drawer-link--logout"
            @click="handleLogout"
          >
            {{ t('account.logout') }}
            <span class="site-header__drawer-arrow" aria-hidden="true">→</span>
          </button>
          <a
            v-else
            :href="'/login'"
            class="site-header__drawer-link site-header__drawer-link--utility"
            @click.prevent="handleNavItem('/login')"
          >
            {{ t('nav.login') }}
            <span class="site-header__drawer-arrow" aria-hidden="true">→</span>
          </a>
        </div>

        <!-- 抽屉底部：语言切换（仅移动端可见，因为桌面顶栏已显示） -->
        <div class="site-header__drawer-footer">
          <LanguageSwitch menu-id="drawer-lang" />
        </div>
      </nav>
    </aside>
  </header>
</template>

<style scoped>
.site-header {
  position: sticky;
  top: 0;
  z-index: 100;
  background-color: var(--yfb-header-bg);
  -webkit-backdrop-filter: blur(var(--yfb-glass-blur));
  backdrop-filter: blur(var(--yfb-glass-blur));
  color: var(--yfb-ink);
  border-bottom: 1px solid var(--yfb-glass-border);
}

.site-header__inner {
  max-width: 1280px;
  margin: 0 auto;
  padding: var(--yfb-sp-4) var(--yfb-sp-6);
  display: flex;
  align-items: center;
  gap: var(--yfb-sp-7);
  position: relative;
  z-index: 102;
}

.site-header__brand-link {
  flex: 1;
  min-width: 0;
  flex-shrink: 1;
  display: inline-flex;
  align-items: center;
  gap: calc(var(--yfb-sp-2) + var(--yfb-sp-1) / 2);
  text-decoration: none;
  overflow: hidden;
}

.site-header__nav {
  display: flex;
  gap: var(--yfb-sp-7);
  flex: 1;
}

.site-header__link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--yfb-ink);
  text-decoration: none;
  font-size: var(--yfb-fs-sm);
  font-weight: 600;
  letter-spacing: var(--yfb-ls-button);
  padding: var(--yfb-sp-1) var(--yfb-sp-3);
  border: 1px solid transparent;
  border-radius: 999px;
  transition: border-color var(--yfb-anim-fast) ease-out, background-color var(--yfb-anim-fast) ease-out, color var(--yfb-anim-fast) ease-out, text-shadow var(--yfb-anim-fast) ease-out;
}

.site-header__link:hover {
  color: var(--yfb-accent);
  text-shadow: 0 0 10px var(--yfb-glow);
}

/* 选中态：霓虹描边胶囊（淡青底 + 青字 + 描边 + 微光），与渐变 CTA 拉开层级 */
.site-header__link--active {
  color: var(--yfb-nav-active-fg, var(--yfb-accent));
  background: var(--yfb-nav-active-bg, rgba(0, 255, 255, 0.08));
  border-color: rgba(0, 255, 255, 0.45);
  text-shadow: 0 0 10px var(--yfb-glow);
}

.site-header__tools {
  display: flex;
  align-items: center;
  gap: var(--yfb-sp-3);
  flex-shrink: 0;
}

/* 登录 / 已登录账号入口：几何统一走全局 .site-header__tool（tokens.css），此处只负责配色 */
.site-header__login {
  color: var(--yfb-on-accent);
  background-image: var(--yfb-grad-cta);
  box-shadow: 0 0 14px var(--yfb-glow);
  text-decoration: none;
  max-width: 10rem;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;
  transition: filter var(--yfb-anim-fast), box-shadow var(--yfb-anim-fast);
}

.site-header__login:hover {
  filter: brightness(1.1);
  box-shadow: 0 0 20px var(--yfb-glow);
}

.site-header__login--account {
  color: var(--yfb-accent);
  background: var(--yfb-surface-2);
  box-shadow: none;
  max-width: 12.5rem;
}

.site-header__login--account:hover {
  box-shadow: 0 0 14px var(--yfb-glow);
}

/* 已登录账号下拉：昵称按钮 + 菜单面板 */
.site-header__acct {
  position: relative;
}

.site-header__acct-avatar {
  display: grid;
  place-items: center;
  width: 22px;
  height: 22px;
  flex-shrink: 0;
  font-family: var(--yfb-font-heading);
  font-size: var(--yfb-fs-xs);
  font-weight: 700;
  line-height: 1;
  color: var(--yfb-on-accent);
  background-image: var(--yfb-grad-cta);
  border-radius: 999px;
}

.site-header__acct-name {
  display: inline-block;
  min-width: 0;
  max-width: 7rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
}

.site-header__acct-id {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
}

/* 会员徽章（用户名旁）：PRO=渐变 / 试用=accent 描边 / 过期=置灰 */
.site-header__member-badge {
  flex-shrink: 0;
  padding: 1px 6px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.06em;
  line-height: 1.4;
  border-radius: 999px;
}

.site-header__member-badge--active {
  color: var(--yfb-on-accent);
  background-image: var(--yfb-grad-cta);
  border: 1px solid var(--yfb-rule);
  box-shadow: 0 0 8px var(--yfb-glow);
}

.site-header__member-badge--trial {
  color: var(--yfb-accent);
  border: 1px solid color-mix(in srgb, var(--yfb-accent) 45%, var(--yfb-rule));
}

.site-header__member-badge--expired {
  color: var(--yfb-ink-soft);
  border: 1px solid var(--yfb-rule);
}

.site-header__acct-menu {
  position: absolute;
  right: 0;
  top: calc(100% + var(--yfb-sp-2));
  min-width: 11rem;
  padding: var(--yfb-sp-2);
  background: var(--yfb-glass-bg-strong);
  -webkit-backdrop-filter: blur(var(--yfb-glass-blur));
  backdrop-filter: blur(var(--yfb-glass-blur));
  border: 1px solid var(--yfb-glass-border);
  border-radius: var(--yfb-radius-md);
  box-shadow: 0 0 18px var(--yfb-glow), 0 16px 40px var(--yfb-drop);
  z-index: 106;
}

.site-header__acct-item {
  display: flex;
  align-items: center;
  gap: var(--yfb-sp-3);
  width: 100%;
  padding: var(--yfb-sp-2) var(--yfb-sp-3);
  border: 0;
  background: transparent;
  color: var(--yfb-ink);
  font-family: inherit;
  font-size: var(--yfb-fs-sm);
  font-weight: 600;
  letter-spacing: 0.02em;
  text-align: left;
  text-decoration: none;
  border-radius: var(--yfb-radius-sm);
  cursor: pointer;
  transition: background-color var(--yfb-anim-fast) ease-out, color var(--yfb-anim-fast) ease-out;
}

.site-header__acct-item:hover {
  background: var(--yfb-accent);
  color: var(--yfb-on-accent);
}

/* 移动抽屉：退出登录行（红色强调，与「前往个人中心」区分） */
.site-header__drawer-link--logout {
  color: var(--yfb-signal-danger);
}

/* 未订阅登录用户常驻开通 CTA：与登录按钮同款渐变（默认 .site-header__login 已是渐变底） */
.site-header__login--upgrade {
  letter-spacing: 0.02em;
}

/* hamburger 按钮（移动端显示在顶栏最左，对应左侧抽屉） */
.site-header__hamburger {
  display: none;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: var(--yfb-sp-1);
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  background-color: var(--yfb-surface);
  border: 1px solid var(--yfb-rule);
  border-radius: var(--yfb-radius-sm);
  cursor: pointer;
  padding: 0;
  font-family: inherit;
  position: relative;
  z-index: 105;
  transition: border-color var(--yfb-anim-fast) ease-out, box-shadow var(--yfb-anim-fast) ease-out;
}

.site-header__hamburger:hover {
  border-color: var(--yfb-accent);
  box-shadow: 0 0 12px var(--yfb-glow);
}

.site-header__hamburger-line {
  display: block;
  width: 16px;
  height: 2px;
  background-color: var(--yfb-accent);
  transition: transform var(--yfb-anim-fast) ease-out, opacity var(--yfb-anim-fast) ease-out;
}

.site-header__hamburger-line--open:nth-child(1) {
  transform: translateY(6px) rotate(45deg);
}

.site-header__hamburger-line--open:nth-child(2) {
  opacity: 0;
}

.site-header__hamburger-line--open:nth-child(3) {
  transform: translateY(-6px) rotate(-45deg);
}

/* ===== 抽屉 ===== */
.site-header__backdrop {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0);
  z-index: 200;
  cursor: pointer;
  pointer-events: none;
  transition: background-color var(--yfb-anim-base) ease-out;
}

.site-header__backdrop--visible {
  background-color: var(--yfb-veil);
  pointer-events: auto;
}

.site-header__drawer {
  position: fixed;
  top: 0;
  left: 0;
  width: 80%;
  max-width: 320px;
  height: 100vh;
  background-color: var(--yfb-glass-bg-strong);
  -webkit-backdrop-filter: blur(var(--yfb-glass-blur));
  backdrop-filter: blur(var(--yfb-glass-blur));
  color: var(--yfb-ink);
  border-right: 1px solid var(--yfb-glass-border);
  z-index: 300;
  transform: translateX(-100%);
  transition: transform var(--yfb-anim-drawer) cubic-bezier(0.16, 1, 0.3, 1);
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.site-header__drawer--open {
  transform: translateX(0);
}

.site-header__drawer-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--yfb-sp-4) var(--yfb-sp-5);
  border-bottom: 1px solid var(--yfb-glass-border);
  flex-shrink: 0;
}

.site-header__drawer-brand {
  text-decoration: none;
  display: inline-flex;
  align-items: center;
}

.site-header__drawer-close {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--yfb-sp-1);
  width: 36px;
  height: 36px;
  background-color: var(--yfb-surface);
  border: 1px solid var(--yfb-rule);
  border-radius: var(--yfb-radius-sm);
  cursor: pointer;
  padding: 0;
  font-family: inherit;
  transition: border-color var(--yfb-anim-fast) ease-out, box-shadow var(--yfb-anim-fast) ease-out;
}

.site-header__drawer-close:hover {
  border-color: var(--yfb-accent);
  box-shadow: 0 0 12px var(--yfb-glow);
}

.site-header__drawer-close-line {
  display: block;
  width: 14px;
  height: 2px;
  background-color: var(--yfb-accent);
}

.site-header__drawer-close-line:nth-child(1) {
  transform: translateY(4px) rotate(45deg);
}

.site-header__drawer-close-line:nth-child(2) {
  transform: translateY(-4px) rotate(-45deg);
}

.site-header__drawer-nav {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: var(--yfb-sp-4) 0;
}

.site-header__drawer-group {
  display: flex;
  flex-direction: column;
  padding: var(--yfb-sp-2) 0;
}

.site-header__drawer-label {
  display: block;
  padding: var(--yfb-sp-2) var(--yfb-sp-5) var(--yfb-sp-1);
  font-family: var(--yfb-font-heading);
  font-size: var(--yfb-fs-xs);
  font-weight: 700;
  letter-spacing: var(--yfb-ls-eyebrow);
  color: var(--yfb-ink-soft);
}

.site-header__drawer-link {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--yfb-sp-4) var(--yfb-sp-5);
  color: var(--yfb-ink);
  text-decoration: none;
  font-size: var(--yfb-fs-md);
  font-weight: 600;
  letter-spacing: var(--yfb-ls-button);
  border-bottom: 1px solid var(--yfb-rule-soft);
  transition: background-color var(--yfb-anim-fast) ease-out, color var(--yfb-anim-fast) ease-out;
}

/* 抽屉主行：图标 + 文字同色联动（hover/选中色随链接继承） */
.site-header__drawer-link-main {
  display: inline-flex;
  align-items: center;
  gap: 10px;
}

.site-header__drawer-link:hover {
  background-color: var(--yfb-accent);
  color: var(--yfb-on-accent);
}

/* 抽屉选中态：淡青底 + 青字（与桌面胶囊同源 token，无左侧条纹） */
.site-header__drawer-link--active {
  background: var(--yfb-nav-active-bg, rgba(0, 255, 255, 0.08));
  color: var(--yfb-nav-active-fg, var(--yfb-accent));
}

.site-header__drawer-link--active .site-header__drawer-arrow {
  color: var(--yfb-nav-active-fg, var(--yfb-accent));
}

.site-header__drawer-link--utility {
  font-size: var(--yfb-fs-sm);
  font-weight: 500;
  letter-spacing: 0;
}

.site-header__drawer-arrow {
  font-weight: 700;
  font-size: var(--yfb-fs-md);
  color: var(--yfb-accent);
}

.site-header__drawer-link:hover .site-header__drawer-arrow {
  color: var(--yfb-on-accent);
}

.site-header__drawer-divider {
  height: 0;
  border-top: 1px solid var(--yfb-rule-soft);
  margin: var(--yfb-sp-2) 0;
}

/* 抽屉底部 footer（语言切换容器） */
.site-header__drawer-footer {
  margin-top: auto;
  padding: var(--yfb-sp-4) var(--yfb-sp-5);
  border-top: 1px solid var(--yfb-rule-soft);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--yfb-sp-3);
  flex-shrink: 0;
}

@media (max-width: 767px) {
  .site-header__inner {
    padding: var(--yfb-sp-3) var(--yfb-sp-4);
    gap: var(--yfb-sp-3);
  }
  .site-header__nav {
    display: none;
  }
  .site-header__hamburger {
    display: flex;
  }
  /* 移动端隐藏语言切换器（移到抽屉里），节省空间 */
  .site-header__tools .lang-switch {
    display: none;
  }
  /* 移动端品牌名 + slogan 单行 ellipsis 截断，避免换行 2 行 */
  .site-header__brand-link :deep(.brand-mark) {
    min-width: 0;
    overflow: hidden;
  }
  .site-header__brand-link :deep(.brand-mark__name) {
    min-width: 0;
    overflow: hidden;
  }
  .site-header__brand-link :deep(.brand-mark__name-main),
  .site-header__brand-link :deep(.brand-mark__slogan) {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
  }
  .site-header__brand-link :deep(.brand-mark__slogan) {
    font-size: 10px;
    letter-spacing: var(--yfb-ls-button);
  }
}

/* 中屏 (480-767px) 进一步压缩工具 */
@media (max-width: 480px) {
  .site-header__inner {
    padding: var(--yfb-sp-2) var(--yfb-sp-3);
    gap: calc(var(--yfb-sp-2) + var(--yfb-sp-1) / 2);
  }
  .site-header__tools {
    gap: var(--yfb-sp-2);
  }
}

@media (min-width: 768px) {
  /* 桌面端不显示抽屉控件（背板 + drawer 隐藏） */
  .site-header__backdrop,
  .site-header__drawer {
    display: none;
  }
}
</style>
